import { KARAOKE_TRACK_NUMBER } from '@src/editor/constants';

import { createDefaultCropKeyframe } from './CropTransformUtils';
import { LayoutSegment } from './LayoutSegment';
import { LayoutTemplateRegistry } from './LayoutTemplateRegistry';

import type { CropTransformKeyframe, TrackTransforms } from './types';

/**
 * Factory for creating LayoutSegment instances with consistent binding patterns.
 * Automatically includes karaoke bindings when the template has a karaoke slot.
 * LayoutEngine handles missing tracks gracefully at render time.
 */
export class LayoutSegmentFactory {
  /**
   * Creates a LayoutSegment with bindings for the provided track.
   * Binds the track to ALL non-karaoke slots in the template.
   * If the template has a karaoke slot, automatically includes karaoke binding to track 32.
   *
   * @param layoutTemplateId - The layout template ID to use
   * @param primaryTrackId - The track ID to bind to all non-karaoke slots
   * @param crop - Optional crop to apply to non-karaoke slots (defaults to full-frame)
   * @returns A new LayoutSegment instance
   */
  static create(
    layoutTemplateId: string,
    primaryTrackId: string,
    crop?: CropTransformKeyframe['crop'],
  ): LayoutSegment {
    const bindings = this.createBindings(
      layoutTemplateId,
      primaryTrackId,
      crop,
    );

    return new LayoutSegment({
      layoutTemplateId,
      bindings,
    });
  }

  /**
   * Creates bindings object with primary track and karaoke track.
   * Binds the provided track to ALL non-karaoke slots.
   * Karaoke slot (if present) is bound to track 32.
   * LayoutEngine will handle missing tracks gracefully at render time.
   *
   * @param layoutTemplateId - The layout template ID to check for slots
   * @param primaryTrackId - The track ID to bind to all non-karaoke slots
   * @param crop - Optional crop to apply to non-karaoke slots (defaults to full-frame)
   * @returns Bindings object with all slots bound appropriately
   */
  static createBindings(
    layoutTemplateId: string,
    primaryTrackId: string,
    crop?: CropTransformKeyframe['crop'],
  ): Record<string, TrackTransforms> {
    const bindings: Record<string, TrackTransforms> = {};

    // Use provided crop or default to full-frame
    const effectiveCrop = crop ?? this.getDefaultCrop();

    // Get template to discover slot names and types
    const registry = LayoutTemplateRegistry.getInstance();
    const template = registry.getTemplate(layoutTemplateId);

    if (!template) {
      // Fallback: assume 'main' slot if template not found
      bindings.main = {
        trackId: primaryTrackId,
        transformKeyframes: [
          createDefaultCropKeyframe({ crop: effectiveCrop }),
        ],
      };
      return bindings;
    }

    // Bind tracks to all slots
    for (const layoutElement of template.layoutElements) {
      const slotName = layoutElement.name;

      if (slotName === 'karaoke') {
        // Karaoke slot always binds to track 32 with default crop
        bindings.karaoke = {
          trackId: String(KARAOKE_TRACK_NUMBER),
          transformKeyframes: [
            createDefaultCropKeyframe({ crop: this.getDefaultCrop() }),
          ],
        };
      } else {
        // All other slots bind to primaryTrackId with the specified crop
        bindings[slotName] = {
          trackId: primaryTrackId,
          transformKeyframes: [
            createDefaultCropKeyframe({ crop: effectiveCrop }),
          ],
        };
      }
    }

    return bindings;
  }

  static getDefaultCrop(): CropTransformKeyframe['crop'] {
    return { x: 0, y: 0, w: 1, h: 1 };
  }
}
